import { FundingPolicyType, UnitedStatesState } from '@shared';
import { assetCareUnderwritingForm, nationwideUnderwritingForm } from './formMetadata';
import { assetCareApplicationForm, nationwideApplicationForm, careScoutApplicationForm } from './applicationFormMetadata';
import { policyFormDefs } from './policyFormRegistry';

export function getFormsByType(formType: PolicyFormType): PolicyFormConfig[] {
  return Object.values(policyFormDefs).filter(form => form.formMetadata.formType === formType);
}

export function getAllUnderwritingForms(): PolicyFormConfig[] {
  return getFormsByType('pre-underwriting-form');
}

export function getAllApplicationForms(): PolicyFormConfig[] {
  return getFormsByType('application-form');
}

export const getFormIdForPolicyTypeAndState = (
  policyType: FundingPolicyType,
  stateOfResidence: keyof typeof UnitedStatesState,
  formType: PolicyFormType,
): string | undefined => {
  const policyFormIdMap = policyTypeAndStateApplicationFormId[policyType];
  if (!policyFormIdMap) {
    return undefined;
  }

  const forms = policyFormIdMap[stateOfResidence] || policyFormIdMap['default'];

  // Handle partner-pre-underwriting-form by using the same form as pre-underwriting-form
  const actualFormType =
    formType === 'partner-pre-underwriting-form' ? 'pre-underwriting-form' : formType;

  return forms[actualFormType as keyof PolicyFormTypeMapping];
};

// =======================
// === LOOKUP BY POLICY TYPE AND FORM TYPE ===
// =======================
export const policyTypeUnderwritingFormId: Partial<Record<FundingPolicyType, string>> = {
  [FundingPolicyType.assetCare]: assetCareUnderwritingForm.formId,
  [FundingPolicyType.jointAssetCare]: assetCareUnderwritingForm.formId,
  [FundingPolicyType.careMattersTwo]: nationwideUnderwritingForm.formId,
  [FundingPolicyType.jointCareMatters]: nationwideUnderwritingForm.formId,
};

const careScoutFormMapping = {
  AZ: {
    'application-form': careScoutApplicationForm.AZ.formId,
  },
  CT: {
    'application-form': careScoutApplicationForm.CT.formId,
  },
  DC: {
    'application-form': careScoutApplicationForm.DC.formId,
  },
  DE: {
    'application-form': careScoutApplicationForm.DE.formId,
  },
  IL: {
    'application-form': careScoutApplicationForm.IL.formId,
  },
  KS: {
    'application-form': careScoutApplicationForm.KS.formId,
  },
  ND: {
    'application-form': careScoutApplicationForm.ND.formId,
  },
  MD: {
    'application-form': careScoutApplicationForm.MD.formId,
  },
  OR: {
    'application-form': careScoutApplicationForm.OR.formId,
  },
  SC: {
    'application-form': careScoutApplicationForm.SC.formId,
  },
  SD: {
    'application-form': careScoutApplicationForm.SD.formId,
  },
  TX: {
    'application-form': careScoutApplicationForm.TX.formId,
  },
  VT: {
    'application-form': careScoutApplicationForm.VT.formId,
  },
  WI: {
    'application-form': careScoutApplicationForm.WI.formId,
  },
  default: {
    'application-form': careScoutApplicationForm.default.formId,
  },
};

const nationwideFormMapping = {
  AZ: {
    'application-form': nationwideApplicationForm.AZ.formId,
  },
  CA: {
    'application-form': nationwideApplicationForm.CA.formId,
  },
  CT: {
    'application-form': nationwideApplicationForm.CT.formId,
  },
  DC: {
    'application-form': nationwideApplicationForm.DC.formId,
  },
  DE: {
    'application-form': nationwideApplicationForm.DE.formId,
  },
  FL: {
    'application-form': nationwideApplicationForm.FL.formId,
  },
  IN: {
    'application-form': nationwideApplicationForm.IN.formId,
  },
  ND: {
    'application-form': nationwideApplicationForm.ND.formId,
  },
  NJ: {
    'application-form': nationwideApplicationForm.NJ.formId,
  },
  SC: {
    'application-form': nationwideApplicationForm.SC.formId,
  },
  SD: {
    'application-form': nationwideApplicationForm.SD.formId,
  },
  default: {
    'application-form': nationwideApplicationForm.default.formId,
  },
};

const policyTypeAndStateApplicationFormId: Partial<
  Record<FundingPolicyType, Record<keyof typeof UnitedStatesState, Partial<PolicyFormTypeMapping>>>
> = {
  [FundingPolicyType.assetCare]: {
    CA: {
      'application-form': assetCareApplicationForm.CA.formId,
    },
    FL: {
      'application-form': assetCareApplicationForm.FL.formId,
    },
    default: {
      'application-form': assetCareApplicationForm.default.formId,
    },
  },
  [FundingPolicyType.jointAssetCare]: {
    CA: {
      'application-form': assetCareApplicationForm.CA.formId,
    },
    FL: {
      'application-form': assetCareApplicationForm.FL.formId,
    },
    default: {
      'application-form': assetCareApplicationForm.default.formId,
    },
  },
  [FundingPolicyType.careMattersTwo]: nationwideFormMapping,
  [FundingPolicyType.jointCareMatters]: nationwideFormMapping,
  [FundingPolicyType.careScoutCareAssurance]: careScoutFormMapping,
};
